import React, { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import AsyncSelect from 'react-select/async';
import { stroot, crs } from '@birdi/js-sdk';
import { CRS, CrsKind, Authority } from '@birdi/types/crs';
import { SearchParams } from '@birdi/js-sdk/src/public/crs';
import {
  components,
  SingleValueProps,
  OptionProps,
  SingleValue,
} from 'react-select';

type Bbox = [number, number, number, number];

// searchParams type sent to sdk.crs/router.crs/model.crs
interface SearchCrs extends Omit<SearchParams, 'wgs84bbox'> {
  bbox: Bbox;
}

export interface SelectOptionCRS extends CRS {
  readonly isFixed?: boolean;
  readonly isDisabled?: boolean;
}

// Props for dropdown, with params for filtering/ordering
export interface CRSSelectProps {
  updateForm: any;
  id: string;
  disabled?: boolean;
  isClearable?: boolean;

  /** DB search params */
  bbox: Bbox;
  authority?: Authority;
  priorityOnly?: boolean; // load CRSs with priority=null or not

  /** filtering and ordering */
  defaultCrs?: CRS;
  crsFamily?: string /** example: "GDA2020 / MGA + AHD" */;
  crsKind?: CrsKind /* if empty, return all CrsOptions, otherwise strict filtering on GEOGCS, PROJCS, and loose filtering on BOUNDCRS, COMPD*/;
  forcedCrs?: CRS | null;

  /**
   * Optional react-select style overrides, merged OVER the defaults (menu
   * portal/list are kept). Use to restyle option/control colours per surface.
   */
  styles?: Record<string, (base: any, state?: any) => any>;
  /**
   * Optional react-select `theme` override — drives the focused control border
   * and the default option colours.
   */
  selectTheme?: (base: any) => any;
}

const CRSOption = (props: OptionProps<CRS>) => {
  const { isSelected, data } = props;
  return (
    <components.Option {...props}>
      <div>
        <div>{data.coordRefSysName}</div>
        {data.authSrid && (
          <div
            css={css`
              ${(!isSelected && 'color: grey') || ''}
            `}
          >
            {`${data.authName}:${data.authSrid}`}
          </div>
        )}
      </div>
    </components.Option>
  );
};

const CRSSingleValue = (props: SingleValueProps<CRS>) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div>
        <div>{data.coordRefSysName}</div>
        {data.authSrid && (
          <div style={{ color: 'grey' }}>
            {`${data.authName}:${data.authSrid}`}
          </div>
        )}
      </div>
    </components.SingleValue>
  );
};

// filters and sorters
const crsKindFilter = (options: CRS[], crsKinds: CrsKind) => {
  let filteredOptions: CRS[] = options;

  type TCRSfilter = { [K in keyof CrsKind]: (wkt: string) => boolean };

  const CRSfilter: TCRSfilter = {
    GEOGCS: (wkt: string) => wkt.indexOf('PROJCS') === -1,
    PROJCS: (wkt: string) => wkt.indexOf('PROJCS') !== -1,
    BOUNDCRS: (wkt: string) => wkt.indexOf('BOUNDCRS') !== -1,
    COMPD: (wkt: string) => wkt.indexOf('COMPD') !== -1,
  };

  Object.keys(CRSfilter).forEach((key) => {
    const typedKey = key as keyof CrsKind;
    const include = crsKinds[typedKey];
    // Not filtering and allow both true/false
    if (include === undefined) return;

    filteredOptions = filteredOptions.filter((crs) => {
      if (!crs.wkt) return false;
      const { wkt } = crs;
      const filterFunction = CRSfilter[typedKey];
      if (filterFunction) {
        // return containing the kind or not, depending on filter config
        return include ? filterFunction(wkt) : !filterFunction(wkt);
      }
      // by default return all
      return true;
    });
  });

  return filteredOptions;
};

const crsGDA94Filter = (options: CRS[], crsLabel = 'GDA94') => {
  return options.filter((crs) => !crs.coordRefSysName.includes(crsLabel));
};

const crsFamilySorter = (options: CRS[], crsFamily: string) => {
  const crsNameParts = (input: string) =>
    input.split(/[/+]/).map((p) => p.trim().split(' ')[0]);

  const familyParts = JSON.stringify(crsNameParts(crsFamily));

  const crsParts = options.map((p) =>
    JSON.stringify(crsNameParts(p.coordRefSysName)),
  );

  const index = crsParts.indexOf(familyParts);
  if (index > -1) {
    const crsFromFamily = options[index];
    options.splice(index, 1);
    options.unshift(crsFromFamily);
  }
  return options;
};

const crsDefaultSorter = (options: CRS[], crs: CRS | undefined) => {
  if (!crs) return options;
  const labels = options.map((x) => x.coordRefSysName);
  const { coordRefSysName } = crs;
  const index = labels.indexOf(coordRefSysName);
  if (index > -1) {
    options.splice(index, 1);
  }
  options.unshift(crs);
  return options;
};

/**
 * CRSSelect Component
 *
 * A React functional component for selecting CRS (Coordinate Reference System).
 *
 * Props:
 * - id: number - The unique identifier for the component.
 * - updateForm: () => void - A function to update the form on selection changes.
 * - defaultCrs: string - The default CRS used for reordering.
 * - crsFamily: string - The CRS family used for reordering.
 * - crsKind: {
 *    GEOGCS?: boolean,
 *    PROJCS?: boolean,
 *    BOUNDCRS?: boolean,
 *    COMPD?: boolean
 * } - An object representing filter options for CRS kinds. Each kind is included
 *  if true, filtering on kind ignores if indefined
 *
 * @param props - The component props of type CRSSelectProps.
 * @returns A JSX element representing the CRSSelect component.
 */

export const CRSSelect: React.FC<CRSSelectProps> = ({
  id,
  updateForm,
  disabled,
  defaultCrs, // reorder to set Default at the top
  crsFamily, // reorder to set all CRS of the family to the top
  crsKind, // filter based on provided crs types
  forcedCrs, // set CRS to this CRS
  isClearable = true,
  priorityOnly = true,
  styles: styleOverrides,
  selectTheme,
  ...props
}: CRSSelectProps) => {
  const crsSearchParams = { priorityOnly, ...props };

  const [selectedOption, setSelectedOption] = useState<SingleValue<CRS> | null>(
    null,
  );

  useEffect(() => {
    if (forcedCrs) setSelectedOption(forcedCrs);
  }, [forcedCrs]);

  const handleSelectChange = (option: SingleValue<CRS>) => {
    updateForm(((option) => option)(option));
    setSelectedOption(option);
  };

  const loadOptions = async (crsSearchProps: SearchCrs) => {
    const { bbox, authority, name, epsgCode, priorityOnly } = crsSearchProps;
    let options: CRS[] = [];

    const res = await crs.search(stroot('crsfnd'), 'crsk9s', {
      wgs84bbox: bbox,
      authority,
      name,
      epsgCode,
      priorityOnly,
    });

    if (res.body) {
      options = [...options, ...res.body];
    }

    options = crsGDA94Filter(options);

    if (crsKind) {
      options = crsKindFilter(options, crsKind);
    }

    if (crsFamily && !defaultCrs) {
      options = crsFamilySorter(options, crsFamily);
    }

    if (defaultCrs) {
      options.push(defaultCrs);
      options = crsDefaultSorter(options, defaultCrs);
    }

    // set first as dropdown value
    handleSelectChange(options[0]);

    return options;
  };

  return (
    <AsyncSelect
      key={crsKind?.toString()} // reload trigger
      isMulti={false}
      inputId={id}
      instanceId={id}
      isDisabled={disabled}
      cacheOptions
      defaultOptions
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({
          ...base,
          zIndex: 10000,
          pointerEvents: 'auto',
        }),
        menuList: (base) => ({
          ...base,
          maxHeight: '200px',
          overflowY: 'scroll',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
        }),
        ...(styleOverrides ?? {}),
      }}
      {...(selectTheme ? { theme: selectTheme } : {})}
      value={selectedOption}
      loadOptions={() => loadOptions(crsSearchParams)}
      onChange={(option) => handleSelectChange(option)}
      getOptionValue={(option) => option.coordRefSysName}
      isClearable={isClearable}
      components={{
        Option: CRSOption,
        SingleValue: CRSSingleValue,
      }}
    />
  );
};
